import { API_BASE_URL, API_ENDPOINTS } from '@/shared/apiClient';
import { authService } from '@/features/auth/services/authService';
import { safeMarkdownLinkUrl } from '@/features/docs/utils/markdownUrlPolicy';

export const safeTaskChatArtifactUrl = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const safeUrl = safeMarkdownLinkUrl(value);
  if (!safeUrl || safeUrl.includes('\\')) return null;
  try {
    const parsed = new URL(safeUrl, 'https://task-chat.invalid');
    return ['http:', 'https:'].includes(parsed.protocol) && !parsed.username && !parsed.password
      ? safeUrl : null;
  } catch {
    return null;
  }
};

export type TaskChatMessageKind = 'user' | 'agent_suggestion' | 'status' | 'artifact' | 'decision';

export interface TaskChatMessage {
  id: string;
  kind: TaskChatMessageKind;
  body: string;
  artifactUrl?: string | null;
  authorUserId?: string | null;
  authorLabel?: string;
  createdAt: string;
}

const normalizeMessage = (message: Record<string, unknown>): TaskChatMessage | null => {
  const id = typeof message.id === 'string' ? message.id : '';
  const body = typeof message.body === 'string' ? message.body : '';
  const createdAt = typeof message.created_at === 'string'
    ? message.created_at
    : (typeof message.createdAt === 'string' ? message.createdAt : '');
  if (!id || !body || !createdAt) {
    return null;
  }

  return {
    id,
    kind: (typeof message.kind === 'string' ? message.kind : 'user') as TaskChatMessageKind,
    body,
    artifactUrl: typeof message.artifact_url === 'string'
      ? message.artifact_url
      : (typeof message.artifactUrl === 'string' ? message.artifactUrl : null),
    authorUserId: typeof message.author_user_id === 'string'
      ? message.author_user_id
      : (typeof message.authorUserId === 'string' ? message.authorUserId : null),
    authorLabel: typeof message.author_label === 'string'
      ? message.author_label
      : (typeof message.authorLabel === 'string' ? message.authorLabel : ''),
    createdAt,
  };
};

const normalizeMessages = (payload: unknown): TaskChatMessage[] => {
  const rawMessages = (
    payload &&
    typeof payload === 'object' &&
    'data' in payload &&
    payload.data &&
    typeof payload.data === 'object' &&
    'messages' in payload.data
      ? payload.data.messages
      : payload &&
        typeof payload === 'object' &&
        'messages' in payload
        ? payload.messages
        : undefined
  );

  if (!Array.isArray(rawMessages)) {
    throw new Error('Invalid task chat response');
  }

  return rawMessages
    .map((message) => (message && typeof message === 'object' ? normalizeMessage(message as Record<string, unknown>) : null))
    .filter((message): message is TaskChatMessage => Boolean(message));
};

export const taskChatService = {
  async list(projectId: string, taskId: string, signal?: AbortSignal): Promise<TaskChatMessage[]> {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.projects.taskChat(projectId, taskId)}`,
      {
        method: 'GET',
        credentials: 'include',
        signal,
      }
    );

    if (!response.ok) {
      throw new Error('Failed to load task chat');
    }

    return normalizeMessages(await response.json());
  },

  async create(
    projectId: string,
    taskId: string,
    body: string,
    kind: TaskChatMessageKind = 'user',
  ): Promise<TaskChatMessage[]> {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.projects.taskChat(projectId, taskId)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body, kind }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to send task chat message');
    }

    return normalizeMessages(await response.json());
  },
};
